import { SWEEP_DIRECTIONS, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
export function makeStimulus(trialIdx, durationMs, level) {
    const variant = variantForLevel(level);
    const direction = SWEEP_DIRECTIONS[Math.floor(Math.random() * SWEEP_DIRECTIONS.length)];
    return {
        trialIdx,
        durationMs: Math.max(timing.minRenderMs, durationMs),
        direction,
        fmOctaves: variant.fmOctaves,
        centerHz: variant.centerHz,
    };
}
export function createSoundSweepsAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    let audioCtx = null;
    let activeOsc = null;
    let activeGain = null;
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function ensureCtx() {
        if (typeof window === 'undefined')
            return null;
        if (audioCtx === null) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const Ctor = (window.AudioContext ?? window.webkitAudioContext);
            if (!Ctor)
                return null;
            audioCtx = new Ctor();
        }
        return audioCtx;
    }
    function stopActive() {
        if (activeOsc) {
            try {
                activeOsc.stop();
            }
            catch {
                /* ignored */
            }
            try {
                activeOsc.disconnect();
            }
            catch {
                /* ignored */
            }
            activeOsc = null;
        }
        if (activeGain) {
            try {
                activeGain.disconnect();
            }
            catch {
                /* ignored */
            }
            activeGain = null;
        }
    }
    function playSweep(stim) {
        return new Promise((resolve) => {
            const ctx = ensureCtx();
            if (!ctx) {
                // Fallback : skip audio, attendre durée logique (pour anti-cheat / replay).
                timers.push(setTimeout(() => resolve(), stim.durationMs));
                return;
            }
            const factor = Math.pow(2, stim.fmOctaves / 2);
            const fLow = stim.centerHz / factor;
            const fHigh = stim.centerHz * factor;
            const startF = stim.direction === 'up' ? fLow : fHigh;
            const endF = stim.direction === 'up' ? fHigh : fLow;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(startF, ctx.currentTime);
            osc.frequency.linearRampToValueAtTime(endF, ctx.currentTime + stim.durationMs / 1000);
            // Attaque/décrue rapides pour éviter clicks.
            gain.gain.setValueAtTime(0, ctx.currentTime);
            gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.01);
            gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + (stim.durationMs - 10) / 1000);
            gain.gain.linearRampToValueAtTime(0, ctx.currentTime + stim.durationMs / 1000);
            osc.connect(gain).connect(ctx.destination);
            activeOsc = osc;
            activeGain = gain;
            osc.start();
            osc.stop(ctx.currentTime + stim.durationMs / 1000);
            timers.push(setTimeout(() => {
                stopActive();
                resolve();
            }, stim.durationMs + 30));
        });
    }
    return {
        mount() { },
        unmount() {
            clearTimers();
            stopActive();
            listeners.clear();
            if (audioCtx) {
                try {
                    void audioCtx.close();
                }
                catch {
                    /* ignored */
                }
                audioCtx = null;
            }
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'pre_stimulus', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'playing' });
                    void playSweep(stim).then(() => {
                        timers.push(setTimeout(() => {
                            update({ subPhase: 'awaiting_response' });
                            trialStart = Date.now();
                        }, timing.postStimulusMs));
                    });
                }, timing.preStimulusMs));
            });
        },
        async showFeedback(correct) {
            update({ subPhase: 'feedback', feedback: correct ? 'correct' : 'incorrect' });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() { },
        resume() { },
        abort() {
            clearTimers();
            stopActive();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleDirectionPick(direction) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const correct = direction === stim.direction;
            const rt = Date.now() - trialStart;
            const resp = { pick: direction, correct, reactionTimeMs: rt };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map